import React, { memo, useRef } from 'react';
import {
    StyledArrowWrapper,
    StyledMarketingSpotImageWrapper,
    StyledMarketingSpot,
    StyledSignUpButton,
    StyledSignUpButtonTexts,
    StyledTextBlockForContentCards,
} from './styled';
import { MarketingSpot as MarketingSpotType } from '~/lib/data-contract';
import { useIsInitialRenderState } from '$shared/hooks';
import { Image, Text } from '$shared/components';
import { useThemeShade } from '$theme';
import { useClubState } from '$features/club/hooks/useClubState';
import { useTranslation } from '$shared/utils';
import { useTheme } from '@emotion/react';
import RightArrow from '$icons/arrow-right.svg';

export type MarketingSpotComponentProps = {
    marketingSpot: MarketingSpotType;
    index: number;
};

const MarketingSpotComponent = ({ marketingSpot, index }: MarketingSpotComponentProps) => {
    const {
        column,
        digizuiteImage,
        headline,
        subHeadline,
        callToAction,
        signupCallToAction,
    } = marketingSpot;
    const { isInitialRender } = useIsInitialRenderState();
    const card = useRef<HTMLDivElement | null>(null);
    const { toggleSignUpActive, toggleSignUpActiveKeepScroll } = useClubState();
    const { translate } = useTranslation();
    const { colors } = useTheme();
    const moduleTheme = useThemeShade({ backgroundColor: colors.dark });

    const imageSize = {
        height: 501,
        width: 768,
    };

    const objectPosition = digizuiteImage?.focalPoint
        ? `${digizuiteImage?.focalPoint[0] * 100}% ${digizuiteImage?.focalPoint[1] * 100}%`
        : undefined;

    const hasTextBlock = Boolean(headline || subHeadline);
    const callToActionUrl = signupCallToAction ? '' : callToAction?.url || ''; // ignore callToAction if signupCallToAction is true
    const isDouble = column === 'Double';

    return (
        <StyledMarketingSpot
            href={callToActionUrl}
            renderEmptyLinksAsDiv
            key={index}
            ref={card}
            isDouble={isDouble}
            type="router"
        >
            <StyledMarketingSpotImageWrapper
                isDouble={isDouble}
                stretch={!hasTextBlock && !signupCallToAction}
            >
                {digizuiteImage?.src && (
                    <Image
                        {...imageSize}
                        src={digizuiteImage.src}
                        loading={
                            isInitialRender && index !== undefined && index < 8 ? 'eager' : 'lazy'
                        }
                        alt={digizuiteImage?.alt || ''}
                        focalPoint={digizuiteImage?.focalPoint}
                        style={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            objectPosition,
                        }}
                    />
                )}
            </StyledMarketingSpotImageWrapper>

            {signupCallToAction && (
                <StyledSignUpButton
                    backgroundColor={moduleTheme.backgroundColor}
                    title={translate('club.signUp.dialogHeader')}
                    onClick={() => {
                        toggleSignUpActive(true);
                        toggleSignUpActiveKeepScroll(true);
                    }}
                >
                    <StyledSignUpButtonTexts>
                        {headline && (
                            <Text
                                children={headline}
                                variant="display5"
                                shade={moduleTheme?.headlineShade}
                            />
                        )}
                        {subHeadline && (
                            <Text shade={moduleTheme?.textShade} variant="caption" lineClamp={3}>
                                {subHeadline}
                            </Text>
                        )}
                    </StyledSignUpButtonTexts>
                    <StyledArrowWrapper hideOnMobile={!isDouble}>
                        <RightArrow />
                    </StyledArrowWrapper>
                </StyledSignUpButton>
            )}

            {hasTextBlock && !signupCallToAction && (
                <StyledTextBlockForContentCards
                    textLineClamp={3}
                    hideArrowOnMobile={!isDouble}
                    headline={headline}
                    moduleTheme={moduleTheme}
                    text={subHeadline}
                    textPosition={'below'}
                    headlineVariant="display5"
                    callToAction={callToAction}
                />
            )}
        </StyledMarketingSpot>
    );
};

export const MarketingSpot = memo(MarketingSpotComponent);
